import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Phone, Star, ShieldCheck } from "lucide-react";
import { SERVICES, CREDENTIALS, COMPANY_PROFILE } from "../data/services";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";

const ServiceBlock = ({ s }) => (
  <article id={s.slug} data-testid={`service-detail-${s.slug}`} className="scroll-mt-28 border-t border-[#0A0A0A] pt-12 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
    <div className="lg:col-span-4">
      <span className="font-heading text-6xl font-black text-[#F5D5B0]">{s.num}</span>
      <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-[-0.03em] text-[#0A0A0A] leading-[1.02] mt-2">{s.title}</h2>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500" data-testid={`service-category-${s.slug}`}>Amazon SPN category: <span className="text-[#6B1414] font-bold">{s.amazonCategory}</span></p>
      <p className="mt-4 font-body text-base text-[#6B7280] leading-relaxed">{s.short}</p>
      <div className="mt-8 space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1414] font-bold">Who it is for</p>
        <p className="font-body text-sm text-[#0A0A0A] leading-relaxed">{s.audience}</p>
      </div>
      <div className="mt-8 space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1414] font-bold">Seller Central access we request</p>
        <ul className="space-y-1.5">
          {s.roles.map((r) => <li key={r} className="font-body text-xs text-[#0A0A0A] leading-relaxed flex gap-2"><span className="w-1 h-1 rounded-full bg-[#2E3A9E] mt-2 shrink-0" />{r}</li>)}
        </ul>
        <p className="font-body text-[11px] text-gray-500 leading-relaxed">Granted by you in Seller Central &gt; Settings &gt; User Permissions; revocable at any time. See our <Link to="/security" className="underline text-[#6B1414]">Security &amp; Data Protection</Link> page.</p>
      </div>
      <a href="#consult" className="mt-8 inline-flex items-center gap-2 font-body font-bold text-sm text-[#0A0A0A] border-b-2 border-[#6B1414] pb-1 hover:text-[#6B1414]">Request this service <ArrowRight size={14} /></a>
    </div>

    <div className="lg:col-span-8 space-y-10">
      <div className="space-y-4">
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1414] font-bold">What the service entails</h3>
        {s.overview.map((p, i) => <p key={i} className="font-body text-base text-[#0A0A0A]/85 leading-[1.8]">{p}</p>)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-md p-6">
          <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1414] font-bold mb-4">What we deliver</h3>
          <ul className="space-y-2.5">
            {s.offerings.map((o) => <li key={o} className="flex gap-2.5 font-body text-sm text-[#0A0A0A] leading-relaxed"><Check size={15} className="text-[#2E3A9E] shrink-0 mt-1" />{o}</li>)}
          </ul>
        </div>
        <div className="space-y-8">
          <div className="bg-[#0A0A0A] text-white rounded-md p-6">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C8956A] font-bold mb-4">Benefits to your business</h3>
            <ul className="space-y-2.5">
              {s.benefits.map((b) => <li key={b} className="flex gap-2.5 font-body text-sm text-white/85 leading-relaxed"><Star size={14} className="text-[#C8956A] shrink-0 mt-1" />{b}</li>)}
            </ul>
          </div>
          <div className="border border-[#E5E5E5] rounded-md p-6">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1414] font-bold mb-4">Our expertise</h3>
            <ul className="space-y-2.5">
              {s.expertise.map((e) => <li key={e} className="flex gap-2.5 font-body text-sm text-[#0A0A0A] leading-relaxed"><ShieldCheck size={15} className="text-[#6B1414] shrink-0 mt-1" />{e}</li>)}
            </ul>
          </div>
        </div>
      </div>

      <div className="border border-[#C8956A] bg-[#F5D5B0]/30 rounded-md p-6" data-testid={`service-engage-${s.slug}`}>
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1414] font-bold mb-4">How a client engages us</h3>
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {s.engage.map((step, i) => (
            <li key={step} className="flex gap-3 font-body text-sm text-[#0A0A0A] leading-relaxed"><span className="font-heading font-black text-[#6B1414] w-5 shrink-0">{i + 1}.</span>{step}</li>
          ))}
        </ol>
      </div>
    </div>
  </article>
);

export default function ServicesPage({ studies = [] }) {
  const { slug } = useParams();
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!slug) { window.scrollTo(0, 0); return; }
    const t = setTimeout(() => document.getElementById(slug)?.scrollIntoView({ behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, [slug]);

  return (
    <div className="bg-white" data-testid="services-page">
      <section className="pt-36 pb-16 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#C8956A]">Our Services · Calium Seller Services</span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[0.95]">Marketplace services for Amazon sellers, explained in full.</h1>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 space-y-5">
            <p className="font-body text-base text-white/70 leading-relaxed">{COMPANY_PROFILE.description}</p>
            <nav className="flex flex-wrap gap-2" data-testid="services-index">
              {SERVICES.map((s) => <a key={s.slug} href={`#${s.slug}`} className="font-mono text-[10px] uppercase tracking-wider border border-white/20 px-3 py-1.5 rounded-full hover:bg-white hover:text-[#0A0A0A] transition-colors">{s.num} {s.title}</a>)}
              <a href="#company-profile" className="font-mono text-[10px] uppercase tracking-wider border border-[#C8956A] text-[#C8956A] px-3 py-1.5 rounded-full hover:bg-[#C8956A] hover:text-[#0A0A0A] transition-colors">Company profile</a>
              <Link to="/security" data-testid="services-security-link" className="font-mono text-[10px] uppercase tracking-wider border border-[#C8956A] text-[#C8956A] px-3 py-1.5 rounded-full hover:bg-[#C8956A] hover:text-[#0A0A0A] transition-colors">Security &amp; data protection</Link>
            </nav>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-8">
        {SERVICES.map((s) => <ServiceBlock key={s.slug} s={s} />)}
      </section>

      <section id="company-profile" className="scroll-mt-28 py-24 bg-[#F5D5B0]/30 border-y border-[#E5E5E5]" data-testid="company-profile-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#6B1414]">Company profile</span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-black tracking-[-0.03em] text-[#0A0A0A] leading-[0.95]">Who you are working with.</h2>
            <p className="mt-6 font-body text-sm text-[#0A0A0A]/80 leading-relaxed">{COMPANY_PROFILE.description}</p>
            <div className="mt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1414] font-bold mb-2">Service categories offered</p>
              <div className="flex flex-wrap gap-2" data-testid="company-categories">
                {COMPANY_PROFILE.categories.map((c) => <span key={c} className="font-body text-xs font-bold bg-[#0A0A0A] text-[#F5D5B0] px-3 py-1.5 rounded-full">{c}</span>)}
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-8 space-y-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0A0A0A]/10 border border-[#0A0A0A]/10" data-testid="credentials-section">
              {CREDENTIALS.map((c) => (
                <div key={c.k} className="bg-[#FAFAFA] p-6">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1414] font-bold">{c.k}</dt>
                  <dd className="mt-2 font-body text-sm text-[#0A0A0A] leading-relaxed">{c.v}</dd>
                </div>
              ))}
            </dl>
            <div className="bg-white border border-[#E5E5E5] rounded-md p-6 lg:p-8 space-y-4" data-testid="seller-access-section">
              <h3 className="font-heading text-xl font-bold text-[#0A0A0A]">How sellers authorise Calium</h3>
              <ol className="space-y-2">
                {[
                  "Calium is registered as a service provider with Amazon and applies only for the Seller Central roles each service needs (listed under every service above).",
                  "You invite Calium as a secondary user in Seller Central > Settings > User Permissions (or in your Amazon Ads account for advertising), selecting only the permissions for the service you have engaged.",
                  "Our named team members work under their own user access; we never ask for your primary login, password or OTP.",
                  "You can view, change or remove our permissions at any time. At the end of an engagement we ask you to remove our access and we delete any information we hold within 30 days.",
                ].map((t, i) => <li key={t} className="flex gap-3 font-body text-sm text-[#0A0A0A] leading-relaxed"><span className="font-heading font-black text-[#6B1414] w-5 shrink-0">{i + 1}.</span>{t}</li>)}
              </ol>
              <Link to="/security" data-testid="profile-security-link" className="inline-flex items-center gap-2 font-body font-bold text-sm text-[#0A0A0A] border-b-2 border-[#6B1414] pb-1 hover:text-[#6B1414]">Read our Security &amp; Data Protection commitments <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      <section id="success-stories" className="py-24" data-testid="success-stories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="mb-14 max-w-3xl">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#6B1414]">Success stories &amp; client testimonials</span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-black tracking-[-0.03em] text-[#0A0A0A] leading-[0.95]">Results our clients report.</h2>
            <p className="mt-4 font-body text-sm text-[#6B7280]">Figures below are from client accounts we manage; testimonials are provided by the clients named. Client references are available on request.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {studies.map((s) => (
              <div key={s.id} data-testid="services-case-study" className="border border-[#E5E5E5] rounded-md p-6 bg-white relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#6B1414]" />
                <p className="font-mono text-[10px] uppercase tracking-wider text-gray-400">{s.marketplace} · Case study</p>
                <h3 className="font-heading text-xl font-bold mt-1">{s.client_name}</h3>
                <p className="font-heading text-3xl font-black text-[#2E3A9E] mt-3">+{s.growth_percentage}%</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">monthly revenue growth · ₹{Number(s.revenue_before).toLocaleString("en-IN")} → ₹{Number(s.revenue_after).toLocaleString("en-IN")}</p>
                <p className="mt-4 font-body text-sm text-[#0A0A0A]"><strong>Challenge:</strong> {s.challenge}</p>
                <p className="mt-2 font-body text-sm text-[#6B7280]"><strong className="text-[#0A0A0A]">What we did:</strong> {s.solution}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t) => (
              <figure key={t.id} data-testid="services-testimonial" className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-md p-6">
                <div className="flex gap-1 text-[#C8956A] mb-3">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}</div>
                <blockquote className="font-body text-sm text-[#0A0A0A] leading-relaxed">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-wider text-gray-500">{t.name} · {t.role}{t.role && ", "}{t.company} · {t.marketplace} seller</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="consult" className="py-20 bg-[#6B1414] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#F5D5B0]">Get started</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-black tracking-[-0.03em]">Book a free account audit with a consultant.</h2>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/#contact" data-testid="services-contact-cta" className="inline-flex items-center gap-2 rounded-full bg-[#F5D5B0] text-[#0A0A0A] px-6 py-3.5 font-body font-bold text-sm">Request free audit <ArrowRight size={14} /></Link>
            <a href="tel:+917827304742" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3.5 font-body font-bold text-sm hover:bg-white hover:text-[#6B1414] transition-colors"><Phone size={14} /> +91 78273 04742</a>
          </div>
        </div>
      </section>
    </div>
  );
}
